import { useMemo, useState } from "react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { FileText, Search } from "lucide-react";
import { isEmptyQuery, matchesQuery, parseSearchQuery } from "@/lib/searchQuery";

/**
 * Item normalizado de búsqueda. Recibidas y Emitidas construyen esta forma
 * desde sus propios tipos, de modo que el buscador (y sus operadores) sea
 * exactamente el mismo en los dos entornos.
 */
export interface SearchableItem {
  id: string;
  /** Título principal (nombre de archivo o nº factura + cliente). */
  title: string;
  /** Nombre de proveedor o cliente, usado por el operador `prov:`. */
  entityName?: string;
  taxId?: string;
  /** Partes que se muestran como subtítulo, ya filtradas. */
  subtitleParts?: (string | undefined | null)[];
  totalAmount?: number | null;
  /** Texto completo en minúsculas sobre el que se busca por substring. */
  searchValue: string;
}

interface GlobalSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: SearchableItem[];
  onSelectItem: (id: string) => void;
  /** Etiqueta del operador de nombre (proveedor / cliente). */
  entityLabel?: string;
  emptyLabel?: string;
}

const fmtAmount = (n: number | undefined | null): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return n.toLocaleString("es-ES", { style: "currency", currency: "EUR" });
};

const GlobalSearch = ({
  open,
  onOpenChange,
  items,
  onSelectItem,
  entityLabel = "proveedor",
  emptyLabel = "No se encontraron facturas.",
}: GlobalSearchProps) => {
  const [rawInput, setRawInput] = useState("");

  const parsed = useMemo(() => parseSearchQuery(rawInput), [rawInput]);
  const usingOperators =
    !isEmptyQuery(parsed) &&
    (parsed.nif || parsed.supplier || parsed.amountMin !== null || parsed.amountMax !== null);

  // Cuando hay operadores, filtramos en JS y forzamos cmdk a no filtrar más
  // para evitar dobles filtros que oculten resultados válidos.
  const filteredItems = useMemo(() => {
    if (!usingOperators) return items;
    return items.filter((it) =>
      matchesQuery(
        {
          searchValue: it.searchValue,
          supplier: it.entityName || "",
          taxId: it.taxId || "",
          totalAmount: typeof it.totalAmount === "number" ? it.totalAmount : null,
        },
        parsed,
      ),
    );
  }, [items, parsed, usingOperators]);

  return (
    <CommandDialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setRawInput("");
      }}
    >
      <CommandInput
        placeholder={`Busca: ${entityLabel}, NIF, nº · operadores: nif:B12345678  prov:marivuelta  >500€  100-200€`}
        value={rawInput}
        onValueChange={setRawInput}
      />
      <CommandList>
        {usingOperators && (
          <div className="px-3 py-1.5 border-b text-[10px] text-muted-foreground flex items-center gap-1.5">
            <Search className="h-3 w-3" />
            <span>
              Filtros activos:
              {parsed.nif && <span className="ml-1 font-mono">NIF={parsed.nif}</span>}
              {parsed.supplier && <span className="ml-1 font-mono">prov~{parsed.supplier}</span>}
              {parsed.amountMin !== null && <span className="ml-1 font-mono">≥{parsed.amountMin}€</span>}
              {parsed.amountMax !== null && <span className="ml-1 font-mono">≤{parsed.amountMax}€</span>}
            </span>
          </div>
        )}
        <CommandEmpty>{emptyLabel}</CommandEmpty>
        <CommandGroup heading={`${filteredItems.length} de ${items.length} facturas`}>
          {filteredItems.map((item) => (
            <CommandItem
              key={item.id}
              // Si usamos operadores, ya hemos filtrado nosotros: pasamos el id
              // como value para que cmdk no filtre por substring otra vez.
              value={usingOperators ? item.id : item.searchValue}
              onSelect={() => {
                onSelectItem(item.id);
                onOpenChange(false);
                setRawInput("");
              }}
              className="cursor-pointer"
            >
              <FileText className="mr-2 h-4 w-4 text-muted-foreground" />
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-sm font-medium truncate">{item.title}</span>
                <span className="text-xs text-muted-foreground truncate">
                  {(item.subtitleParts ?? []).filter(Boolean).join(" · ")}
                </span>
              </div>
              <span className="ml-2 shrink-0 text-xs tabular-nums text-muted-foreground">
                {fmtAmount(item.totalAmount)}
              </span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

export default GlobalSearch;
